import { useState, useEffect } from "react";
import { LotteryDto, LotteryWinnersDto } from "./api/Dtos";
import { DELETE, PUT, POST, GET } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import { HStack, VStack, Button, Spacer, Tooltip } from "@chakra-ui/react";
import { FastTable } from "./Table/FastTable";
import { useLotteriesFromBackend, useLotteryWinnersFromBackend } from "./Services/LotteryService";
import AddLotteryModal from "./Modals/AddLotteryModal";
import { PromptForConfirmationModal } from "./Modals/PromptForConfirmationModal";

const modifyFields = [
  "name",
  "description",
  "nrOfWinners",
  "disciplinesCond",
  "startnumberSequencesCond",
  "teamNameCond",
  "completedCond",
];

const DISPLAY_NAME_NAME = "Vinst namn";
const DISPLAY_NAME_DESCRIPTION = "Beskrivning";
const DISPLAY_NAME_NROFWINNERS = "Antal vinnare";
const DISPLAY_NAME_DISCIPLINESCOND  = "Klasser";
const DISPLAY_NAME_STARTNUMBERSEQUENCESCOND = "Startnummerintervall";
const DISPLAY_NAME_TEAMNAMECOND = "Lagnamn";
const DISPLAY_NAME_COMPLETEDCOND = "Har tid i mål";

const modifyDisplayLabels = [
  DISPLAY_NAME_NAME,  
  DISPLAY_NAME_DESCRIPTION,
  DISPLAY_NAME_NROFWINNERS,
  DISPLAY_NAME_DISCIPLINESCOND,
  DISPLAY_NAME_STARTNUMBERSEQUENCESCOND,
  DISPLAY_NAME_TEAMNAMECOND,
  DISPLAY_NAME_COMPLETEDCOND,
];

export function Lotteries() {
  const { lotteries, setLotteries } = useLotteriesFromBackend();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const globalContext = useGlobalContext();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedLotteryName, setSelectedLotteryName] = useState<string | null>(null);
  const { lotteryWinners, setLotteryWinners } = useLotteryWinnersFromBackend(selectedId) // useState<Array<LotteryWinnersDto>>([]);
  const [showConfirmDeleteWinners, setShowConfirmDeleteWinners] = useState(false);

  // Get competitions from backend
  useEffect(() => {
    console.log(
      "Lotteries component rendered with lotteries:",
      lotteries
    );
    if (loading) {
        setLoading(false);
    }
    setError(
      globalContext.competition?.id !== undefined
        ? ""
        : "Ingen tävling är vald."
    );
  }, [lotteries]);


  if (loading) return <div>Loading...</div>;
  if (error.length > 0) return <div>Fel: {error}</div>;

  const addLottery = async (newLottery: LotteryDto) => {
    console.info(
      "add: " +
        newLottery.nrOfWinners +
        ", id: " +
        newLottery.id.toString()
    );
    if (globalContext.competition?.id !== undefined) {
      const success = await POST.lottery(newLottery);
      if (success) {
        console.log("Added lottery:", newLottery);
        setLotteries((old) => [...old, newLottery]);
      }
    } else {
      console.error("Ingen tävling är vald.");
    }
  };

  const drawWinners = async () => {
    if (selectedId == null || selectedId <= 0) return;

    const result = await POST.lotteryDraw(selectedId);
    if (result.startsWith("Ok")) {
      console.log("Winners drawn, msg:", result);
      // re‑fetch the winners (or if the server returns them, use that value)
      const winners = await GET.allLotteryWinnersInLottery(selectedId);
      setLotteryWinners(winners);
    } else {
      console.error("Failed to draw winners: ", result);
    }
  };

  const deleteWinners = async () => {
     if (!selectedId) return;

    const result = await DELETE.lotteryWinnersByLotteryId(selectedId);
    if (result) {
      // re‑fetch the winners (or if the server returns them, use that value)
      const winners = await GET.allLotteryWinnersInLottery(selectedId);
      setLotteryWinners(winners);
    } else {
      console.error("Failed to delete winners: ", result);
    }
  };

  return (
    <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        {
          <VStack>
            <HStack>
              <Tooltip label={"Lägg till priser som ska lottas ut. Kan baseras på olika villkor som startgrupp, startnummer, lag, etc."}>
                <Button
                  onClick={() => setIsModalOpen(true)}
                  colorScheme="green"
                  padding={"40px"}
                  fontSize={"24px"}
                >
                  Lägg till pris att lotta ut
                </Button>
              </Tooltip>
            </HStack>
            <AddLotteryModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onAddRow={addLottery}
            />
          </VStack>
        }
      </HStack>

      <FastTable
        data={lotteries}
        height={200}
        pageHeader={`Priser till ${globalContext.competition?.tag ?? "Ingen tävling vald"}`}
        repeatHeaderRows={3}
        downloadFileName="Lotterier"
        headers={[
            DISPLAY_NAME_NAME,
            DISPLAY_NAME_DESCRIPTION,
            DISPLAY_NAME_NROFWINNERS,
            DISPLAY_NAME_DISCIPLINESCOND,
            DISPLAY_NAME_STARTNUMBERSEQUENCESCOND,
            DISPLAY_NAME_TEAMNAMECOND,
            DISPLAY_NAME_COMPLETEDCOND,
        ]}

        forceDisplayToTypeMap={
          new Map([
            [DISPLAY_NAME_NAME, "string"], 
            [DISPLAY_NAME_DESCRIPTION, "string"],
            [DISPLAY_NAME_NROFWINNERS, "number"],
            [DISPLAY_NAME_DISCIPLINESCOND, "string"],
            [DISPLAY_NAME_STARTNUMBERSEQUENCESCOND, "string"],
            [DISPLAY_NAME_TEAMNAMECOND, "string"],
            [DISPLAY_NAME_COMPLETEDCOND, "boolean"],
          ])
        }
        rowFormat={"1fr 2fr 1fr 2fr 2fr 2fr 1fr 1fr 1fr"}
        toRowData={(p) => {
          return [
            p.name,
            p.description,
            p.nrOfWinners,
            p.disciplinesCond,
            p.startnumberSequencesCond,
            p.teamNameCond,
            p.completedCond,
          ];
        }}
        modifiableFields={modifyFields as Array<keyof LotteryDto>}
        modifiableFieldsDisplayName={modifyDisplayLabels}
        onChange={async (updatedLottery, oldLottery) => {
          if (
            globalContext.competition?.id !== undefined &&
            updatedLottery.name != null &&
            updatedLottery.nrOfWinners != null && 
            updatedLottery.nrOfWinners >= 0
            
          ) {
            const success = await PUT.lottery(
              updatedLottery,
              globalContext.competition?.id
            );
            if (success) {
              setLotteries((old) => {
                return old.map((p) =>
                  p.id === updatedLottery.id ? updatedLottery : p
                );
              });
            }
            return success;
          } else {
            console.error("Ingen tävling är vald.");
          }
          return false;
        }}
        onDelete={async (deletedLottery) => {
          const lottery =
            deletedLottery;
          console.info(
            "Delete: " +
              lottery.id +
              ", name: " +
              lottery.name
          );
          const success = await DELETE.lottery(lottery);
          if (success) {
            setLotteries((old) => {
              return old.filter((p) => p.id != deletedLottery.id);
            });
          }
          return true; // success;
        }}
        onRowSelect={(row) => {
          setSelectedId(row?.id ?? null);
          setSelectedLotteryName(row?.name ?? null);
          console.log("Selected lottery:", row);
        }}
      />
      <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        {
          <VStack>
            <HStack>
              <Tooltip label={"Lotta vinnare till priset"}>
                <Button
                  onClick={() => drawWinners()}
                  isDisabled={selectedId == null || selectedId <= 0}
                  colorScheme="green"
                  padding={"40px"}
                  fontSize={"24px"}
                >
                  Lotta vinnare till priset
                </Button>
              </Tooltip>
              <Tooltip label={"Radera alla vinnare till priset"}>
                <Button
                  onClick={() => setShowConfirmDeleteWinners(true)}
                  isDisabled={selectedId == null || selectedId <= 0}
                  colorScheme="red"
                  padding={"40px"}
                  fontSize={"18px"}
                >
                  Radera vinnarna till priset
                </Button>
              </Tooltip>              
            </HStack>
            {showConfirmDeleteWinners && (
            <PromptForConfirmationModal
              request={"Är du säker på att du vill radera alla vinnare för detta pris?"}
              onConfirm={deleteWinners}
              onClose={() => setShowConfirmDeleteWinners(false)}
              confirmButtonTitle="Radera"
              abortButtonTitle="Avbryt"
              colorScheme="red"
            />
          )}
          </VStack>
        }
      </HStack>
      </div>
      {selectedId != null && selectedId > 0 && (
      <FastTable
        data={lotteryWinners}
        height={250}
        pageHeader={`Vinnare till pris: ${selectedLotteryName}`}
        repeatHeaderRows={3}  // Freeze/repeat first 3 rows (page header + blank + column headers)
        downloadFileName="Lotterivinnare"
        headers={[
            "Startnr",
            "Namn",
            "Född",
            "Klubb/lag",
        ]}

        rowFormat={"1fr 2fr 1fr 2fr"}
        toRowData={(pw) => {
          return [
            pw.startnumber,
            pw.name,
            pw.birthYear,
            pw.clubOrTeam,
          ];
        }}
      />
      )}
    </div>
  );
}

export default Lotteries;
